import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { PageIcon, TextBoxIcon, PressButtonIcon } from "@easy/ui/icons";
import type { BlockType, BlockDefWithType, BlockTypeGroup } from "./block.interfaces";
import { useHeros } from "./useHeros";
import { useStats } from "./useStats";
import { useNavigations } from "./useNavigations";
import { useCtas } from "./useCtas";
import { useFeatures } from "./useFeatures";
import { makeLogger } from "@/shared/consola";
import { useLogos } from "./useLogos";
import { useTeams } from "@/apps/page/services/blocks/useTeams";
import { useLongForms } from "./useLongForms";
import { useBanners } from "./useBanners";
import { useTables } from "./useTables";
import { useBentos } from "./useBentos";

const groupTypes: BlockTypeGroup[] = [
    {
        group: "texts",
        name: "Texts",
        icon: TextBoxIcon,
    },
    {
        group: "buttons",
        name: "Buttons",
        icon: PressButtonIcon,
    },
    {
        group: "stats",
        name: "Stats",
        icon: TextBoxIcon,
    },
    {
        group: "navigation",
        name: "Navigation",
        icon: TextBoxIcon,
    },
    {
        group: "images",
        name: "Images",
        icon: TextBoxIcon,
    },
    {
        group: "details",
        name: "Details",
        icon: TextBoxIcon,
    },
    {
        group: "socialProofImages",
        name: "Social proof images",
        icon: TextBoxIcon,
    },
    {
        group: "features",
        name: "Features",
        icon: TextBoxIcon,
    },
    {
        group: "logos",
        name: "Logos",
        icon: TextBoxIcon,
    },
    {
        group: "members",
        name: "Members",
        icon: TextBoxIcon,
    },
    {
        group: "items",
        name: "Items",
        icon: TextBoxIcon,
    },
];

const logger = makeLogger(useBlocks.name);

export function useBlocks() {
    const { t } = useI18n();

    const { blocks: heroBlocks } = useHeros();
    const { blocks: statsBlocks } = useStats();
    const { blocks: navigationBlocks } = useNavigations();
    const { blocks: ctaBlocks } = useCtas();
    const { blocks: featuresBlocks } = useFeatures();
    const { blocks: logosBlocks } = useLogos();
    const { blocks: teamsBlocks } = useTeams();
    const { blocks: longFormsBlocks } = useLongForms();
    const { blocks: bannerBlocks } = useBanners();
    const { blocks: tablesBlocks } = useTables();
    const { blocks: bentoBlocks } = useBentos();

    const blocks = computed(() => [
        ...heroBlocks.value,
        ...statsBlocks.value,
        ...navigationBlocks.value,
        ...ctaBlocks.value,
        ...featuresBlocks.value,
        ...logosBlocks.value,
        ...teamsBlocks.value,
        ...longFormsBlocks.value,
        ...bannerBlocks.value,
        ...tablesBlocks.value,
        ...bentoBlocks.value,
    ]);

    const blockTypes = computed<BlockType[]>(() => [
        {
            type: "nav",
            title: t("page.blocks.nav_sections"),
            icon: PageIcon,
        },
        {
            type: "hero",
            title: t("page.blocks.hero_sections"),
            icon: PageIcon,
        },
        {
            type: "features",
            title: t("page.blocks.features_sections"),
            icon: PageIcon,
        },
        {
            type: "team",
            title: t("page.blocks.team_sections"),
            icon: PageIcon,
        },
        {
            type: "tabs",
            title: t("page.blocks.tabs_sections"),
            icon: PageIcon,
        },
        {
            type: "bentoGrid",
            title: t("page.blocks.bentoGrid_sections"),
            icon: PageIcon,
        },
        {
            type: "tables",
            title: t("page.blocks.tables_sections"),
            icon: PageIcon,
        },
        {
            type: "banner",
            title: t("page.blocks.banner_sections"),
            icon: PageIcon,
        },
        {
            type: "contact",
            title: t("page.blocks.contact_sections"),
            icon: PageIcon,
        },
        {
            type: "stats",
            title: t("page.blocks.stats_sections"),
            icon: PageIcon,
        },
        {
            type: "logos",
            title: t("page.blocks.logos_sections"),
            icon: PageIcon,
        },
        {
            type: "header",
            title: t("page.blocks.header_sections"),
            icon: PageIcon,
        },
        {
            type: "cta",
            title: t("page.blocks.cta_sections"),
            icon: PageIcon,
        },
        {
            type: "newsletter",
            title: t("page.blocks.newsletter_sections"),
            icon: PageIcon,
        },
        {
            type: "longform",
            title: t("page.blocks.longform_sections"),
            icon: PageIcon,
        },
        {
            type: "footer",
            title: t("page.blocks.footer_sections"),
            icon: PageIcon,
        },
    ]);

    const getBlocksByType = (type: string) => {
        return blocks.value.filter((block) => block.type === type);
    };

    const getBlockWithTypeMetadata = (typeId: string) => {
        const block = blocks.value.find((block) => block.typeId === typeId);

        logger.debug("getBlockWithTypeMetadata", typeId, block);

        if (!block) {
            return null;
        }

        const blockType = blockTypes.value.find((blockType) => blockType.type === block.type);

        if (!blockType) {
            return null;
        }

        return { block, blockType } as BlockDefWithType;
    };

    const getBlockByValue = (typeId: string) => {
        return blocks.value.find((block) => block.typeId === typeId);
    };

    return {
        getAllBlockTypes: () => {
            return blockTypes.value.map((blockType) => ({
                ...blockType,
                count: blocks.value.filter((block) => block.type === blockType.type).length,
            }));
        },
        getBlocksByType,
        getBlockWithTypeMetadata,
        getBlockByValue,
        groupTypes,
        blocks,
    };
}
